import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { BaseItem, InvoicePayload } from "@/types/invoice";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import MemberForm from "../../modal/form";
import { MemberTableDatatypes } from "@/types/member";
import {
  useGetAllMemberQuery,
  useGetMemberByIdQuery,
} from "@/services/memberApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import Combobox from "@/components/ui/common/combobox";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import {
  useAddLinkedMembersMutation,
  useGetInvoiceByIdQuery,
  useSwapLinkedMembersMutation,
} from "@/services/invoiceApi";
import { LinkedMembers } from "@/types/linked-members";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Switch } from "@/components/ui/switch";
import { displayValue } from "@/lib/helper";
import { MembershipStatus } from "@/types/enums";
interface LinkedMembersProps {
  isOpen: boolean;
  setOpen: any;
  refetch?: any;
  preSelectedMemberId?: string;
  transaction_id: number | null;
}

const LinkedMembersPage = ({
  isOpen,
  setOpen,
  preSelectedMemberId,
  transaction_id,
}: LinkedMembersProps) => {
  const { data: selectedTransactionData, refetch } = useGetInvoiceByIdQuery(
    { id: transaction_id as number },
    {
      skip: transaction_id == null,
    }
  );
  const [selectTransaction, setSelectTransaction] =
    useState<InvoicePayload | null>(null);
  const [allOrgMembersList, setAllOrgMembersList] = useState<
    MemberTableDatatypes[]
  >([]);
  const [assignedMemberIds, setAssignedMemberIds] = useState<number[]>([]);

  const [isAllMembersSearch, setIsAllMembersSearch] = useState<boolean>(true);
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { data: memberByInvoice } = useGetMemberByIdQuery(
    { org_id: orgId, id: selectTransaction?.member_id as number },
    { skip: !selectTransaction?.member_id }
  );

  const { data: memberTableList, refetch: memberRefetch } =
    useGetAllMemberQuery(
      {
        org_id: orgId,
        query: isAllMembersSearch
          ? `sort_key=id&sort_order=desc`
          : `sort_key=id&sort_order=desc&business_id=${selectTransaction?.member_id}`,
      },
      {
        skip: !selectTransaction?.member_id && !memberByInvoice,
      }
    );

  // Store all members when the form loads (only once)
  useEffect(() => {
    if (memberTableList?.data && allOrgMembersList.length === 0) {
      setAllOrgMembersList(memberTableList.data);
    }
  }, [memberTableList]);

  const [action, setAction] = useState<"add" | "edit" | "member-created">(
    "add"
  );
  const [openMemberForm, setOpenMemberForm] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<MemberTableDatatypes | null>(
    null
  );
  const [editableRows, setEditableRows] = useState<Record<number, boolean>>({});
  const [selectedMembers, setSelectedMembers] = useState<
    Record<string, string>
  >({});

  const handleEditClick = (rowIndex: number) => {
    setEditableRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex], // Toggle edit state for the specific row
    }));
  };

  const [swapMemberId, setSwapMemberId] = useState<number | null>(null);
  const [swapMembershipId, setSwapMembershipId] = useState<number | null>(null);
  const [replacementMemberId, setReplacementMemberId] = useState<string>("");

  const [addLinkedMember, result] = useAddLinkedMembersMutation();
  const [swapLinkedMember, swapResult] = useSwapLinkedMembersMutation();

  const getAssignedMemberDetails = (productId: number, seatNum: number) => {
    const product = selectTransaction?.items.find(
      (item) => item.item_id === productId
    );

    if (
      product?.assigned_members &&
      product.assigned_members.length >= seatNum
    ) {
      const assignedMember = product.assigned_members[seatNum - 1];
      const memberDetails = memberList.find(
        (member) => member.id === assignedMember.member_id
      );

      return {
        memberDetails,
        status: assignedMember.membership_plan_status,
      };
    }
    return null;
  };

  function handleOpenForm() {
    setAction("add");
    setEditMember(null);
    setOpenMemberForm(true);
  }

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (selectedTransactionData) {
      setSelectTransaction(selectedTransactionData as InvoicePayload);
      const assignedIds = selectedTransactionData.items?.flatMap(
        (item) => item.assigned_members?.map((member) => member.member_id) || []
      );

      setAssignedMemberIds(assignedIds);
    }
  }, [selectedTransactionData]);

  const memberList = useMemo(() => {
    let updatedList = isAllMembersSearch
      ? [...allOrgMembersList]
      : memberTableList?.data || [];

    // Ensure assigned members are always included
    const assignedMembers = allOrgMembersList.filter((member) =>
      assignedMemberIds.includes(member.id)
    );

    // Merge lists without duplicates
    return [
      ...new Map(
        [...updatedList, ...assignedMembers].map((m) => [m.id, m])
      ).values(),
    ];
  }, [
    isAllMembersSearch,
    memberTableList,
    assignedMemberIds,
    allOrgMembersList,
  ]);

  const memberCombobox = memberList.map((member) => ({
    value: member.id + "",
    label: member.first_name + " " + member.last_name,
  }));

  const handleSwapMember = async (contractId: number, seatNum: number) => {
    if (!replacementMemberId) {
      toast({
        variant: "destructive",
        title: "Please select a replacement member",
      });
      return;
    }

    try {
      const payload = {
        invoice_id: selectTransaction?.id ?? 0,
        member_id: swapMemberId ?? 0,
        replacement_member_id: parseInt(replacementMemberId),
        contract_id: contractId,
      };

      console.log("Swap payload:", payload);

      await swapLinkedMember(payload).unwrap();

      toast({
        variant: "success",
        title: "Member swapped successfully",
      });

      // Reset states
      setSwapMemberId(null);
      setReplacementMemberId("");

      // Refetch all necessary data
      refetch && refetch();
      memberRefetch && memberRefetch();

      // If you have the transaction refetch, call it too
      selectTransaction?.id && refetch && refetch();
      handleClose();
      // Reset any edit states
      setEditableRows({});
      setSavingRows({});
      setSelectedMembers({});
    } catch (error) {
      console.error("Swap error:", error);
      toast({
        variant: "destructive",
        title: "Failed to swap member",
      });
    }
  };

  const [originalMembers, setOriginalMembers] = useState<
    Record<string, string>
  >({});
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});

  const handleMemberSelect = (
    value: string,
    contractId: number,
    seatNum: number
  ) => {
    const key = `${contractId}-${seatNum}`;
    const selectedMember = memberList.find(
      (member) => member.first_name + " " + member.last_name === value
    );

    if (selectedMember) {
      setSelectedMembers((prev) => ({
        ...prev,
        [key]: selectedMember.id.toString(),
      }));
    }
  };

  const handleSave = async (
    contractId: number,
    seatNum: number,
    book_date: string,
    membershipId: number
  ) => {
    const key = `${contractId}-${seatNum}`;
    const selectedMemberId = selectedMembers[key];
    const originalMemberId = originalMembers[key];

    // Validate member selection
    if (!selectedMemberId) {
      toast({
        variant: "destructive",
        title: "Please select a member before saving",
      });
      return;
    }

    setSavingRows((prev) => ({ ...prev, [key]: true }));

    try {
      await addLinkedMember({
        member_id: parseInt(selectedMemberId, 10),
        book_date: book_date,
        membership_plan_id: membershipId,
        contract_id: contractId,
        invoice_id: selectTransaction?.id ?? 0,
      })
        .unwrap()
        .then(() => {
          toast({
            variant: "success",
            title: "Member Linked Successfully",
          });

          // Update original state only on successful save
          setOriginalMembers((prev) => ({
            ...prev,
            [key]: selectedMemberId,
          }));

          refetch && refetch();
          handleEditClick(seatNum);
        });
    } catch (error) {
      // Reset to original value on error
      // setSelectedMembers((prev) => ({
      //   ...prev,
      //   [key]: originalMemberId,
      // }));

      handleSaveError(error);
    } finally {
      setSavingRows((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveError = (error: unknown) => {
    if (error && typeof error === "object" && "data" in error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description:
          typedError.data?.detail ||
          (typedError.data as { message?: string }).message ||
          "An error occurred.",
      });
    } else {
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: "Something went wrong.",
      });
    }
  };

  const renderMemberNameOrDropdown = (transaction: BaseItem, num: number) => {
    const isEditable = editableRows[num] || false;
    const assignedMember = transaction.assigned_members?.[num - 1];

    if (isEditable) {
      return (
        <Combobox
          defaultValue={assignedMember?.member_name || ""}
          setFilter={(value: string) =>
            handleMemberSelect(value, transaction.item_id, num)
          }
          list={memberCombobox}
          label={"Member"}
        />
      );
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setEditableRows({});
      setSavingRows({});
      setSelectedMembers({});
      setSwapMemberId(null);
      setIsAllMembersSearch(true);
    }
  }, [isOpen]);

  const areAllSeatsAssigned = (transaction: BaseItem) => {
    return transaction.assigned_members?.length === transaction.quantity;
  };

  console.log({ selectTransaction, memberList });

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="w-full !max-w-[500px] flex flex-col custom-scrollbar p-0 bg-formbg"
      >
        <SheetHeader className="  p-4 bg-white flex flex-row gap-1 items-center justify-between  sticky top-0 border border-b-1 z-20">
          <div className="flex gap-2">
            <Button
              onClick={handleClose}
              className="border-transparent hover:bg-transparent p-0 pr-2 m-0 bg-transparent"
            >
              <X className="text-black" />
            </Button>
            <div>
              <SheetTitle className="py-0 text-nowrap">
                Linked Members and Seats
              </SheetTitle>
              <p className="m-0 py-0 text-sm">
                Total seats: {selectTransaction?.total_quantity}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleOpenForm}
              className="w-[75px] px-2 text-black h-9"
            >
              Add
            </Button>
          </div>
        </SheetHeader>
        <SheetDescription className="px-4 pb-4 space-y-4 ">
          {
            <div className="flex gap-2">
              <Switch
                disabled={memberByInvoice?.is_business}
                checked={isAllMembersSearch}
                onCheckedChange={(checked) => {
                  setIsAllMembersSearch(checked);
                  memberRefetch();
                }}
              />
              {isAllMembersSearch ? "All Members" : "By Business"}
            </div>
          }
          <Accordion type="multiple" className="w-full space-y-4">
            {selectTransaction?.items
              ?.filter((item) => item.item_type !== "products")
              .map((transaction, i) => (
                <AccordionItem
                  key={transaction.item_id}
                  value={transaction.description}
                  className=""
                >
                  <AccordionTrigger className="!bg-white no-underline px-4 hover:no-underline !rounded-t-md [&[data-state=closed]]:!rounded-md">
                    <div className="flex flex-row w-full justify-between items-start !bg-white">
                      <p className="capitalize text-sm">
                        {transaction.description} -{" "}
                        {displayValue(transaction.booking_date)}
                      </p>

                      <p className="text-sm font-normal">
                        Seats:{" "}
                        <span className="text-gray-400">
                          {transaction.quantity}
                        </span>
                      </p>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="!bg-white !rounded-b-md">
                    <Separator className="w-[95%] py-[1px] mx-auto !text-gray-800" />

                    {/* Generate the quantity list specific to this transaction */}
                    <div className="px-4">
                      {Array.from(
                        { length: transaction.quantity },
                        (_, qtyIndex) => qtyIndex + 1
                      ).map((num) => {
                        //check first row
                        const isFirstRow = num === 1;

                        const isEditable = editableRows[num] || false;

                        const defaultMember = memberList.find(
                          (member) => member.id == selectTransaction.member_id
                        );
                        const memberName = defaultMember
                          ? `${defaultMember?.first_name} ${defaultMember?.last_name}`
                          : "";

                        const assignedMember = getAssignedMemberDetails(
                          transaction.item_id,
                          num
                        );
                        return (
                          <>
                            <div
                              className="flex gap-2 items-center  py-2"
                              key={`${transaction.item_id}-${num}`}
                            >
                              <p>{num}.</p>
                              {!assignedMember ? (
                                isEditable ? (
                                  <Combobox
                                    // defaultValue={isFirstRow ? memberName : ""}
                                    defaultValue={""}
                                    setFilter={(value: string) =>
                                      handleMemberSelect(
                                        value,
                                        transaction.contract_id ?? 0,
                                        num
                                      )
                                    }
                                    list={memberCombobox}
                                    label={"Member"}
                                    disabled={!isEditable}
                                  />
                                ) : (
                                  <span className="text-gray-500">
                                    No assigned member
                                  </span>
                                )
                              ) : (
                                <span className="capitalize">{`${assignedMember.memberDetails?.first_name} ${assignedMember.memberDetails?.last_name}`}</span>
                              )}
                              <div className="flex items-center gap-2">
                                {!areAllSeatsAssigned(transaction) &&
                                  !assignedMember && (
                                    <i
                                      onClick={() =>
                                        isEditable
                                          ? handleSave(
                                              transaction.contract_id as number,
                                              num,
                                              transaction.booking_date ??
                                                new Date()
                                                  .toISOString()
                                                  .split("T")[0],
                                              transaction.item_id
                                            )
                                          : handleEditClick(num)
                                      }
                                      className={`fa ${
                                        savingRows[
                                          `${transaction.item_id}-${num}`
                                        ]
                                          ? "fa-spinner fa-spin"
                                          : !isEditable
                                            ? "fa-pen"
                                            : "fa-floppy-disk text-lg"
                                      } cursor-pointer`}
                                    ></i>
                                  )}
                                {assignedMember && (
                                  <i
                                    onClick={() => {
                                      setSwapMemberId(
                                        assignedMember.memberDetails?.id || null
                                      );
                                      setSwapMembershipId(transaction.item_id);
                                    }}
                                    className="fa fa-right-left cursor-pointer text-white bg-gray-600 p-1.5 rounded-[50%]"
                                    title="Swap Member"
                                  ></i>
                                )}
                              </div>
                            </div>

                            {swapMemberId ===
                              assignedMember?.memberDetails?.id &&
                              swapMembershipId == transaction.item_id && (
                                <div className="px-2">
                                  <p className="px-3">Select Replacement</p>
                                  <div className="gap-2 flex p-2">
                                    <Combobox
                                      defaultValue=""
                                      setFilter={(value: string) => {
                                        const selectedMember =
                                          memberCombobox.find(
                                            (member) => member.label === value
                                          );
                                        console.log(
                                          "Selected member combobox:",
                                          selectedMember
                                        ); // Debug log

                                        if (selectedMember) {
                                          const selectedMemberId = Number(
                                            selectedMember.value
                                          ); // Convert to number

                                          const memberExists =
                                            transaction?.assigned_members?.some(
                                              (assignedMember) =>
                                                assignedMember.member_id ===
                                                selectedMemberId
                                            );

                                          if (memberExists) {
                                            // Show error toast and prevent selection
                                            toast({
                                              variant: "destructive",
                                              description:
                                                "Selected member is already assigned.",
                                            });
                                            return;
                                          }

                                          // If the member does not exist, proceed to set replacement member ID
                                          setReplacementMemberId(
                                            selectedMember.value
                                          );
                                          console.log(
                                            "Setting replacement ID to:",
                                            selectedMember.value
                                          );
                                        }
                                      }}
                                      list={memberCombobox.filter(
                                        (member) =>
                                          member.value !==
                                          swapMemberId?.toString()
                                      )}
                                      label="Member"
                                    />
                                    <Button
                                      onClick={() =>
                                        handleSwapMember(
                                          transaction.contract_id as number,
                                          num
                                        )
                                      }
                                      disabled={!replacementMemberId}
                                    >
                                      {swapResult.isLoading ? (
                                        <i className="fa fa-spinner fa-spin"></i>
                                      ) : (
                                        <i className="fa fa-floppy-disk"></i>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                          </>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>

          <MemberForm
            open={openMemberForm}
            setOpen={setOpenMemberForm}
            memberData={editMember}
            setMemberData={setEditMember}
            action={action}
            setAction={setAction}
            refetch={memberRefetch}
            breadcrumb="Member Details"
          />
        </SheetDescription>
      </SheetContent>
    </Sheet>
  );
};

export default LinkedMembersPage;
